import React, { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Eye, Send } from "lucide-react";
import { AppStatusChoice, Borrower } from "@/types/borrower";
import AppStatusPill from "@/components/layout/AppStatusPill";
import { BADGE_CONFIG } from "@/components/layout/DealStatusBadge";
import FunderChip from "@/components/borrower/FunderChip";
import {
  BorrowerDealMenuItems,
  useBorrowerDealActions,
} from "@/components/borrower/BorrowerDealActions";
import { ContextMenu, ContextMenuContent, ContextMenuTrigger } from "@/components/ui/context-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { formatStatusAge } from "@/utils/formatStatusAge";
import { getUserDisplayName } from "@/utils/emailFormatter";
import { docusealService, parseSendError } from "@/services/docuseal";
import { borrowerService } from "@/services/borrowers";
import { queryKeys } from "@/queryKeys";
import { cn } from "@/lib/utils";
import { useTrialAccount } from "@/hooks/useTrialAccount";

interface WorkflowCardProps {
  borrower: Borrower;
  color: string;
  /** Pre-fetched status choices, passed down from WorkflowBoard so each card
   * doesn't independently subscribe to the same status-transitions query. */
  statusChoices?: AppStatusChoice[];
  /** Shared open/view/edit/assign/delete handlers from the parent column. */
  dealActions: ReturnType<typeof useBorrowerDealActions>;
}

type ActionMessage = { type: "success" | "error"; text: string };

const RESEND_COOLDOWN_MS = 2000;

// Match the "Funder Review" status chip's light-blue treatment (same color,
// same tinted background/border formula as AppStatusPill).
const RESEND_BUTTON_COLOR = BADGE_CONFIG.funder_review.color;
const resendButtonStyle: React.CSSProperties = {
  backgroundColor: `${RESEND_BUTTON_COLOR}18`,
  borderColor: `${RESEND_BUTTON_COLOR}38`,
  color: RESEND_BUTTON_COLOR,
};

const WorkflowCard: React.FC<WorkflowCardProps> = ({
  borrower,
  color,
  statusChoices,
  dealActions,
}) => {
  const queryClient = useQueryClient();
  const age = formatStatusAge(borrower.last_status_changed_at ?? borrower.created_at);
  const showViewedIcon = borrower.app_status === "contract_sent" && borrower.contract_viewed;
  // Prefer company name once intake extracts it; fall back to the inbound
  // email subject so Submitted / Needs Input cards stay distinguishable.
  const companyName = (borrower.company_name || "").trim();
  const emailSubject = (borrower.email_subject || "").trim();
  const isSubjectFallback = !companyName && !!emailSubject;
  const title = companyName || (emailSubject ? `Email Subject: ${emailSubject}` : "Untitled");

  const canResendContract = borrower.app_status === "contract_sent";
  const canResendPlaid = borrower.app_status === "plaid_initiated";
  const { isTrial, recipientEmail: trialRecipientEmail } = useTrialAccount();
  // Trial orgs send merchant mail to the trial user; say so in the hover copy.
  const merchantEmail = isTrial
    ? `${trialRecipientEmail} (your email — trial)`
    : borrower.email || "the merchant";
  const trialCapturedNote =
    isTrial && borrower.email ? ` Captured merchant email: ${borrower.email}.` : "";

  const [resendingContract, setResendingContract] = useState(false);
  const [resendingPlaid, setResendingPlaid] = useState(false);
  const [actionMessage, setActionMessage] = useState<ActionMessage | null>(null);

  // Success messages are transient (the card's own status will catch up on
  // the next poll); errors stay put until the next attempt so they're not
  // missed.
  useEffect(() => {
    if (actionMessage?.type !== "success") return;
    const timer = window.setTimeout(() => setActionMessage(null), 5000);
    return () => window.clearTimeout(timer);
  }, [actionMessage]);

  // Guards against setting state after the card has unmounted (e.g. the
  // borrower moves out of this bucket while a resend is still in flight).
  const isMountedRef = useRef(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const invalidateAfterResend = () => {
    void queryClient.invalidateQueries({ queryKey: ["borrowers"] });
    void queryClient.invalidateQueries({ queryKey: queryKeys.borrower(borrower.id) });
    void queryClient.invalidateQueries({
      queryKey: queryKeys.contractStatus(borrower.id, "main_contract"),
    });
  };

  // Same action as the Contracts tab's "Resend email" button: re-send the
  // invite email for the existing active submission (same signing link, no
  // new contract, no confirmation needed).
  const handleResendContract = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (resendingContract) return;
    setActionMessage(null);
    setResendingContract(true);
    window.setTimeout(() => {
      if (isMountedRef.current) setResendingContract(false);
    }, RESEND_COOLDOWN_MS);
    void (async () => {
      try {
        const status = await docusealService.getStatus(borrower.id, "main_contract");
        if (!status.current) {
          throw new Error("No contract submission was found to resend.");
        }
        const res = await docusealService.resendInviteEmail(status.current.external_id);
        if (!isMountedRef.current) return;
        setActionMessage({
          type: "success",
          text: res.detail || "The signing email was re-sent with the same link.",
        });
        invalidateAfterResend();
      } catch (err) {
        if (!isMountedRef.current) return;
        setActionMessage({ type: "error", text: parseSendError(err).message });
      }
    })();
  };

  // Legacy board resend of the Plaid login email. Final Underwriting no longer
  // sends or copies that link; Merchant Tasks owns new bank-connection flows.
  const handleResendPlaid = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (resendingPlaid) return;
    setActionMessage(null);
    setResendingPlaid(true);
    window.setTimeout(() => {
      if (isMountedRef.current) setResendingPlaid(false);
    }, RESEND_COOLDOWN_MS);
    void (async () => {
      try {
        const res = await borrowerService.plaidResendLink(borrower.id);
        if (!isMountedRef.current) return;
        setActionMessage({
          type: "success",
          text: res.new_link_created ? "New Plaid link created and sent." : "Plaid link resent.",
        });
        invalidateAfterResend();
      } catch {
        if (!isMountedRef.current) return;
        setActionMessage({
          type: "error",
          text: "Could not resend the Plaid link. Please try again.",
        });
      }
    })();
  };

  const menuProps = {
    borrower,
    canAssign: dealActions.canAssign,
    canDelete: dealActions.canDelete,
    onOpenNewTab: (b: Borrower) => dealActions.open(b, { newTab: true }),
    onView: dealActions.view,
    onEdit: dealActions.edit,
    onAssign: dealActions.requestAssign,
    onDelete: dealActions.requestDelete,
  };

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div
          role="button"
          tabIndex={0}
          data-testid="workflow-card"
          className="mb-3 w-full rounded-lg border bg-card p-3 text-left transition-all last:mb-0 hover:-translate-y-px hover:shadow-md"
          style={{ borderColor: "hsl(var(--border))" }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = color;
            e.currentTarget.style.boxShadow = `0 2px 8px ${color}33`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = "";
            e.currentTarget.style.boxShadow = "";
          }}
          onClick={(e) => dealActions.activate(borrower, e)}
          onAuxClick={(e) => {
            if (e.button === 1) dealActions.activate(borrower, e);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              dealActions.view(borrower);
            }
          }}
        >
          <div className="flex items-start justify-between gap-2">
            <p
              className={
                isSubjectFallback
                  ? "truncate text-xs font-medium text-muted-foreground"
                  : "truncate text-sm font-semibold"
              }
              title={title}
            >
              {title}
            </p>
            <span className="shrink-0 text-xs text-muted-foreground">{age}</span>
          </div>
          <div className="mt-2 flex items-center gap-1.5">
            <AppStatusPill status={borrower.app_status} choices={statusChoices} />
            {showViewedIcon && (
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      className="inline-flex shrink-0 items-center justify-center rounded-full bg-amber-100 p-1 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                      aria-label="Contract viewed by recipient"
                    >
                      <Eye className="h-3 w-3" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top">Contract viewed by recipient</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {(canResendContract || canResendPlaid) && (
              <div className="ml-auto flex shrink-0 items-center gap-1">
                {canResendContract && (
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          aria-label="Resend contract email"
                          onClick={handleResendContract}
                          disabled={resendingContract}
                          style={resendButtonStyle}
                          className="inline-flex shrink-0 items-center justify-center rounded-full border p-1 transition-opacity hover:opacity-80 disabled:pointer-events-none disabled:opacity-50"
                        >
                          <Send className="h-3 w-3" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        Resend contract to <span className="font-semibold">{merchantEmail}</span>.
                        {trialCapturedNote}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                {canResendPlaid && (
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          aria-label="Resend plaid login email"
                          onClick={handleResendPlaid}
                          disabled={resendingPlaid}
                          style={resendButtonStyle}
                          className="inline-flex shrink-0 items-center justify-center rounded-full border p-1 transition-opacity hover:opacity-80 disabled:pointer-events-none disabled:opacity-50"
                        >
                          <Send className="h-3 w-3" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        Resend Plaid login to <span className="font-semibold">{merchantEmail}</span>
                        .{trialCapturedNote}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            )}
          </div>
          <p className="mt-2 truncate text-xs text-muted-foreground">
            <span className="font-medium text-foreground/70">Broker:</span>{" "}
            {borrower.broker_email || borrower.broker_name
              ? getUserDisplayName(borrower.broker_name, borrower.broker_email)
              : "—"}
          </p>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">
            <span className="font-medium text-foreground/70">U/W:</span>{" "}
            {borrower.underwriter_email || borrower.underwriter_name
              ? getUserDisplayName(borrower.underwriter_name, borrower.underwriter_email)
              : "Unassigned"}
          </p>
          {borrower.organization_name && (
            <div className="mt-1.5">
              <FunderChip organizationName={borrower.organization_name} />
            </div>
          )}
          {actionMessage && (
            <p
              className={cn(
                "mt-1.5 text-xs",
                actionMessage.type === "success" ? "text-green-600" : "text-destructive"
              )}
            >
              {actionMessage.text}
            </p>
          )}
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent onClick={(e) => e.stopPropagation()}>
        <BorrowerDealMenuItems {...menuProps} variant="context" />
      </ContextMenuContent>
    </ContextMenu>
  );
};

export default WorkflowCard;
